/**
 * Interfaces related to the editor.
 */
import React, { ReactElement, ReactNode } from 'react'

import { PointCloudOctree } from '@datalabs-jp/three-loader'
import { EditorToolKey } from '@editor/config/constants'
import { CursorState } from '@editor/config/constants/cursor'
import { PointCloudProps } from '@editor/pages/projects/editor/objects/PointCloud'

import { Identifiable, LayerStatus, PointArray } from './attribute'
import {
  CircleAnchorProps,
  CuboidAnchorFrameProps,
  CuboidFrameProps,
  CylinderMeshProps,
  Editor,
  LabelProps,
  PolygonPlaneMeshProps,
  PolylineProps,
} from './canvas'
import { CircleMeshProps } from './canvas/circleMesh'
import { MeasurementLabelProps } from './canvas/measurementLabel'
import { RectangleMeshProps } from './canvas/rectangleMesh'
import { TextAnnotationProps } from './canvas/textAnnotation'
import { Drawing } from './drawing'
import { InspectionArea, InspectionItem, InspectionItemType } from './inspection'
import { InspectionGroup } from './inspectionGroup'
import { MaskRegion } from './maskRegion'
import { Shape, ShapeKeyType } from './shape'
import { UserInfo } from './user'

/**
 * Reads the colour of the solid mesh's texture where the pointer hit it, as a `#rrggbb` string.
 * Returns undefined when the pointer is not over a textured mesh, or the texture cannot be read.
 *
 * Lazy on purpose: nothing is sampled unless a tool asks for it.
 */
export type SolidMeshColorSampler = () => string | undefined

/**
 * Extra properties/flags for editor events.
 */
export interface CanvasEventsExtra {
  /**
   * User has clicked on the same point twice.
   */
  isDoubleClicked: boolean

  /**
   * User has clicked on the same point 3 times.
   */
  isTripleClicked: boolean

  /**
   * User has been dragging the canvas.
   */
  isDragged: boolean

  /**
   * Samples the solid mesh's own texture colour under the pointer.
   * Only present when the pointer is over the solid mesh.
   */
  sampleSolidMeshColor?: SolidMeshColorSampler
}

/**
 * All events that could occur on the canvas.
 */
export interface CanvasEvents {
  /**
   * Mouse up event from the canvas.
   *
   * @param e MouseEvent
   * @param points Clicked point in 3D Space
   * @param solidMeshPoint Clicked point on solid meshes in 3D Space
   */
  onMouseDown?: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    points: PointArray | undefined,
    solidMeshPoint: PointArray | undefined,
  ) => void

  /**
   * Raw mouse down event from the canvas.
   * Since this runs before the onMouseDown event, it will not have any extra properties (double click detection, etc)
   * or any click validation.
   *
   * Generally you do not want to use this event, use onMouseDown instead.
   *
   * Note that having this does not prevent onMouseDown from running.
   *
   * @param e MouseEvent
   * @param points Clicked point in 3D Space
   * @param solidMeshPoint Clicked point on solid meshes in 3D Space
   */
  onRawMouseDown?: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    points: PointArray | undefined,
    solidMeshPoint: PointArray | undefined,
  ) => void

  /**
   * Raw mouse up event from the canvas.
   * Since this runs before the onMouseUp event, it will not have any extra properties (double click detection, etc)
   * or any click validation.
   *
   * Generally you do not want to use this event, use onMouseUp instead.
   *
   * Note that having this does not prevent onMouseUp from running.
   *
   * @param e MouseEvent
   * @param points Clicked point in 3D Space
   * @param solidMeshPoint Clicked point on solid meshes in 3D Space
   */
  onRawMouseUp?: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    points: PointArray | undefined,
    solidMeshPoint: PointArray | undefined,
  ) => void

  /**
   * Mouse up event from the canvas.
   * This is only triggered with left click or touch. To handle right click, use onRawMouseUp.
   *
   * Note that having this does not prevent onRawMouseUp from running.
   *
   * @param e MouseEvent
   * @param points Clicked point in 3D Space
   * @param solidMeshPoint Clicked point on solid meshes in 3D Space
   * @param extra Extra properties/flags
   */
  onMouseUp?: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    points: PointArray | undefined,
    solidMeshPoint: PointArray | undefined,
    extra: CanvasEventsExtra,
  ) => void

  /**
   * Move event from the canvas (mouse or touch).
   *
   * @param points Clicked point in 3D Space
   * @param solidMeshPoint Clicked point on solid meshes in 3D Space
   * @param mouseEvent MouseEvent
   * @param touchEvent TouchEvent
   * @param extra Extra properties/flags
   */
  onMove?: (
    points: PointArray | undefined,
    solidMeshPoint: PointArray | undefined,
    mouseEvent?: React.MouseEvent<HTMLDivElement, MouseEvent>,
    touchEvent?: React.TouchEvent<HTMLDivElement>,
    extra?: CanvasEventsExtra,
  ) => void

  /**
   * Touch start event from the canvas.
   *
   * @param touchEvent TouchEvent
   * @param points Clicked point in 3D Space
   * @param solidMeshPoint Clicked point on solid meshes in 3D Space
   */
  onTouchStart?: (
    touchEvent: React.TouchEvent<HTMLDivElement>,
    points: PointArray | undefined,
    solidMeshPoint: PointArray | undefined,
  ) => void

  /**
   * Touch move event from the canvas.
   *
   * @param touchEvent Touch event from the canvas.
   * @param points Current point in 3D Space
   * @param solidMeshPoint Current point on solid meshes in 3D Space
   */
  onTouchMoveCapture?: (
    touchEvent: React.TouchEvent<HTMLDivElement>,
    points: PointArray | undefined,
    solidMeshPoint: PointArray | undefined,
  ) => void

  /**
   * Touch end event from the canvas. End touch events does not provide location of the touch.
   * Use the last known location from the onTouchMove event.
   *
   * @param touchEvent TouchEvent
   * @param extra Extra properties/flags
   */
  onTouchEnd?: (touchEvent: React.TouchEvent<HTMLDivElement>, extra: CanvasEventsExtra) => void

  /**
   * User initiated undo through keyboard shortcut or button.
   */
  onUndo?: () => void

  /**
   * Cancellation of current action or drawing, either through ESC key or cancel button.
   */
  onCancel?: () => void

  /**
   * Completion of current action or drawing through complete button.
   */
  onComplete?: () => void

  /**
   * Focus to a specific inspection item on the canvas.
   * This can be triggered from;
   * 1. The focus button on an inspection item in the info panel.
   * 2. The Hashed ID is contained in the URL as a deep linking mechanism.
   *
   * @param hashedItemId Hashed ID of the inspection item to focus on. Use the hashString function to generate this from the item ID.
   */
  onFocusItem?: (hashedItemId: string) => void | Promise<void>
}
/**
 * All events that could occur on the canvas.
 *
 * An improved version that does not ray trace by default.
 */
export interface CanvasEventsV2 {
  /**
   * Mouse up event from the canvas.
   *
   * @param mouseEvent MouseEvent
   * @param points Clicked point in 3D Space
   * @param extra Extra properties/flags
   */
  onMouseDown?: (mouseEvent: React.MouseEvent<HTMLDivElement, MouseEvent>) => void

  /**
   * Mouse up event from the canvas.
   * This is only triggered with left click or touch. To handle right click, use onRawMouseUp.
   *
   * Note that having this does not prevent onRawMouseUp from running.
   *
   * @param mouseEvent MouseEvent
   * @param points Clicked point in 3D Space
   * @param extra Extra properties/flags
   */
  onMouseUp?: (mouseEvent: React.MouseEvent<HTMLDivElement, MouseEvent>, extra: CanvasEventsExtra) => void

  /**
   * Raw mouse up event from the canvas.
   * This does not handle any click validation or double/triple click detection.
   *
   * @param mouseEvent MouseEvent
   * @returns
   */
  onRawMouseUp?: (mouseEvent: React.MouseEvent<HTMLDivElement, MouseEvent>) => void

  /**
   * Move event from the canvas (mouse or touch).
   *
   * @param points Clicked point in 3D Space
   * @param mouseEvent MouseEvent
   */
  onMouseMove?: (mouseEvent: React.MouseEvent<HTMLDivElement, MouseEvent>) => void

  /**
   * Touch start event from the canvas.
   *
   * @param points Clicked point in 3D Space
   */
  onTouchStart?: (touchEvent: React.TouchEvent<HTMLDivElement>) => void

  /**
   * Touch move event from the canvas.
   *
   * @param points Current point in 3D Space
   */
  onTouchMoveCapture?: (touchEvent: React.TouchEvent<HTMLDivElement>) => void

  /**
   * Touch end event from the canvas. End touch events does not provide location of the touch.
   * Use the last known location from the onTouchMove event.
   */
  onTouchEnd?: (touchEvent: React.TouchEvent<HTMLDivElement>) => void

  /**
   * Touch move event from the canvas.
   *
   * @param touchEvent Touch event from the canvas.
   * @param extra Extra properties/flags
   */
  onTouchMove?: (touchEvent: React.TouchEvent<HTMLDivElement>, extra?: CanvasEventsExtra) => void

  /**
   * User initiated undo through keyboard shortcut or button.
   */
  onUndo?: () => void

  /**
   * User has cancelled the current action, usually by pressing the ESC key.
   */
  onCancel?: () => void
}

/**
 * Objects to be shown on the canvas.
 */
export interface CanvasConfigObjects {
  /**
   * Optimized version of distance label / polyline.
   */
  polylines?: PolylineProps[]

  /**
   * Warning labels to be shown on the canvas.
   */
  labels?: LabelProps[]

  /**
   * Circle anchors to be shown on the canvas.
   */
  circleAnchors?: CircleAnchorProps[]

  /**
   * Circle meshes to be shown on the canvas.
   * These are static meshes and do not have any transform controls.
   */
  circleMeshes?: CircleMeshProps[]

  /**
   * Rectangle meshes to be shown on the canvas.
   * These are static meshes and do not have any transform controls.
   */
  rectangleMeshes?: RectangleMeshProps[]

  /**
   * Polygon plane mesh to be shown on the canvas.
   *
   * @deprecated Use polygonPlaneMeshesV2 instead.
   */
  polygonPlaneMeshes?: PolygonPlaneMeshProps[]

  /**
   * Polygon plane mesh to be shown on the canvas.
   */
  polygonPlaneMeshesV2?: PolygonPlaneMeshProps[]

  /**
   * Transformable polygon plane mesh to be shown on the canvas.
   *
   * Only one can be used/rendered at the same time as various flags and events does not
   * have any identifications to differentiate multiple instances.
   */
  polygonPlaneMeshTransformable?: PolygonPlaneMeshProps[]

  /**
   * Cylinder mesh to be shown on the canvas.
   */
  cylinderMeshes?: (CylinderMeshProps & { volumeId?: string })[]

  /**
   * Text annotations to be shown on the canvas.
   */
  textAnnotations?: TextAnnotationProps[]

  /**
   * Measurement labels to be shown on the canvas.
   */
  measurementLabels?: MeasurementLabelProps[]

  /**
   * Working cuboids to be shown on the canvas.
   */
  cuboidAnchorFrames?: CuboidAnchorFrameProps[]

  /**
   * Drawn cuboids to be shown on the canvas.
   * This cuboid have transform controls and can be adjusted by the user.
   */
  cuboidFrames?: CuboidFrameProps[]

  /**
   * .pcd point clouds to be shown on the canvas.
   * Potree IS NOT SUPPORTED.
   */
  pointClouds?: PointCloudProps[]
}

/**
 * Configuration for the canvas.
 */
export interface CanvasConfig {
  events?: CanvasEvents
  objects?: CanvasConfigObjects
}

/**
 * Configuration for the canvas.
 */
export interface CanvasConfigV2 {
  events?: CanvasEventsV2
  objects?: CanvasConfigObjects
}

/**
 * Props to be accepted by tool hooks.
 */
export interface CanvasProps {
  pointClouds: PointCloudOctree[]
  showLabels?: boolean
}

/**
 * Events for editor buttons.
 */
export interface EditorButtonEvents {
  /**
   * Click event handler.
   */
  onClick: () => void
}

/**
 * Editor button congfiguration.
 */
export interface EditorButton extends EditorButtonEvents {
  /**
   * Unique key of this button. It must be unique across all tools.
   */
  key?: string

  /**
   * Label of the button.
   */
  label?: string

  /**
   * Label to be shown when the action is in progress.
   */
  loadingLabel?: string

  /**
   * Tooltip to be shown when hovering over the button.
   */
  tooltip?: ReactNode

  /**
   * Custom icon to be shown on the button.
   * Defaults to the type of the button.
   */
  icon?: ReactNode

  /**
   * Indicates the tool is processing.
   */
  isLoading?: () => boolean

  /**
   * Whether the button should be shown.
   */
  isShown: () => boolean

  /**
   * Whether the button should be disabled.
   */
  isDisabled: () => boolean
}

/**
 * Buttons to be shown on the bottom-right corner of the canvas.
 */
export interface EditorButtons {
  /**
   * Complete the current action. For tools where the drawing must explicitly be completed
   * such as Polyline or Polygon, this is an alternate way to complete the drawing.
   *
   * Implement the action on `useMainCanvas` event hook `onComplete`.
   */
  complete?: Omit<EditorButton, keyof EditorButtonEvents>

  /**
   * Undo the last action.
   * It does not have any events since it is handled by the editor itself
   * and propagated to event hook in useMainCanvas.
   *
   * Implement the action on `useMainCanvas` event hook `onUndo`.
   */
  undo?: Omit<EditorButton, keyof EditorButtonEvents>

  /**
   * Cancel the current action.
   *
   * Implementation can either be done here through onClick,
   * or through the event hook `onCancel` in useMainCanvas with priority to `onClick`.
   */
  cancel?: Omit<EditorButton, keyof EditorButtonEvents> & Partial<EditorButtonEvents>

  /**
   * Completely reset the current tool. It should set the state back to the initial state.
   */
  reset?: EditorButton

  /**
   * Trigger the save function of the current tool.
   * This will move user back to the MOVE/SELECT tool.
   */
  submit?: EditorButton

  /**
   * Apply the current changes without closing the tool.
   */
  apply?: EditorButton
}

export interface EditorPanelItem {
  /**
   * ID of the item.
   * Can be used for items without `item` property.
   */
  id?: string

  /**
   * Group ID if the item is part of a group.
   *
   * Generally used for when `item` cannot be provided (eg: working items).
   */
  groupId?: string

  /**
   * Key of the item for React list rendering.
   */
  key: string

  /**
   * The actual text name of the item.
   * Should be same as label but string only.
   * Used for default value during editing mode.
   */
  name: string

  /**
   * Custom display label of the item.
   * Defaults to name if not set.
   */
  displayName?: string | ReactElement

  /**
   * Order of the item in the list.
   */
  order?: number

  /**
   * Color of the item.
   */
  color?: string

  /**
   * Hide color picker on the item.
   * Used for when element-style coloring is used to retain color information.
   */
  hideColorPicker?: boolean

  /**
   * Dropdown to be shown on the item.
   * If this is shown, all icons including delete button will be hidden.
   * Only visibility toggle will be shown.
   */
  dropdown?: ReactElement

  /**
   * Item to be shown.
   * Do not fake this with a dummy object. It will be treated as a real object saved in the database.
   */
  item?: Identifiable | (InspectionItem & LayerStatus) | Shape | MaskRegion | InspectionGroup | Drawing

  /**
   * Optional InspectionItem reference for editing purposes.
   * Used when the item is a Shape but we need the InspectionItem for editing the name.
   */
  inspectionItem?: InspectionItem

  /**
   * Mark a virtual container item as a specific shape type.
   * Do not use this for actual shapes.
   */
  shapeKey?: ShapeKeyType

  /**
   * Mark a virtual container item as a specific inspection item type.
   * Do not use this for actual inspection items.
   */
  itemType?: InspectionItemType

  /**
   * Children items.
   */
  children?: EditorPanelItem[]

  /**
   * Flag to indicate that the item has been parented into a group.
   */
  isGrouped?: boolean

  /**
   * Will show a count badge on the item.
   */
  isCountable?: boolean

  /**
   * Flag to indicate that the item can be re-ordered.
   */
  isOrderable?: boolean

  /**
   * Flag to indicate that the item can colapse/expand.
   * By default, an item will automatically be collapsible if it has children.
   *
   * If you want to disable this behavior, set this to false.
   * If you want to force it to be collapsible, set this to true.
   *
   * When set to true, a chevron icon will always be shown on the left side of the item.
   * - If there's no children, the chevron icon will be disabled rather than hidden.
   */
  isCollapsible?: boolean

  /**
   * Flag to indicate that the item can be checked with a checkbox.
   * This is different from 'selected' in the sense that it is a permanent state.
   * Items checked through this should be saved to the database.
   */
  isCheckable?: boolean

  /**
   * Custom flag to indicate that the item is checked.
   *
   * By default, checked detection is done through comparison of ID against checkedElementIds in the toolUtil store.
   * Use this if you want to use the checkbox but not using checkedElementIds.
   */
  isChecked?: boolean

  /**
   * Flag to indicate that the item is marked for deletion.
   * This is different from isDeleting which indicates that the item is in the process of being deleted.
   *
   * Use this to show a special indicator on the item to indicate that it is marked for deletion;
   * - it's name will have a strikethrough
   * - visibility toggle will be disabled (if available)
   * - any clickable actions will be disabled (if available)
   */
  isMarkedForDeletion?: boolean

  /**
   * This will use in conjuction with isCheckable/isChecked to determine if the item is checked
   * through it's children instead of the item itself.
   * This is useful for virtual containers that have children that can be checked.
   * If this is set to true, the item will be checked if all children are checked.
   * If any child is not checked, the item will be unchecked.
   */
  aggregateSelection?: boolean

  /**
   * Layer is in editing mode.
   */
  isEditing?: boolean

  /**
   * Will show a working indicator on the item.
   */
  isWorking?: boolean

  /**
   * Flag to indicate that changes have been made to the item.
   */
  isDirty?: boolean

  /**
   * Flag for a parent item to indicate that it has working items.
   * If any child item has isWorking set to true, this will be set to true.
   *
   * Not to be set manually, but automatically populated during rendering.
   */
  hasWorkingItems?: boolean

  /**
   * Will show a saving indicator on the item.
   * It will also disable certain behaviors such as edit and delete.
   */
  isSaving?: boolean

  /**
   * Will show spinner on where the delete button is while it is being deleted.
   */
  isDeleting?: boolean

  /**
   * Will show an error indicator on the item.
   */
  errorMessage?: string

  /**
   * Will show a loading indicator on the item.
   * This is useful for items that are being processed.
   */
  isLoading?: boolean

  /**
   * Will show a success indicator on the item.
   * This is useful for items that have been processed successfully.
   * It does not come with any additional behavior, just a visual indicator.
   */
  isSuccess?: boolean

  /**
   * Virtual container will behave the same as non-virtual container as far
   * as interactions on the parent that will affect those children.
   * However bubbling up, interactions on the children will not be the same.
   *
   * 1. Auto-toggle of parent
   *   - On non-virtual container, toggling all child will not toggle the parent.
   *   - On virtual container, toggling all child will toggle the parent.
   * 2. Deletion
   *   - On non-virtual container, it will delete the parent only. BE is responsible for deleting children.
   *   - On virtual container, it will collect children IDs and delete them.
   */
  isVirtualContainer?: boolean

  /**
   * If it has children, it will be collapsed by default.
   */
  defaultCollapsed?: boolean

  /**
   * Custom CSS style for the item for the text label.
   */
  textStyle?: React.CSSProperties

  /**
   * Custom item deletion event.
   * If it is false, the item will not be deletable.
   */
  onDelete?: (() => Promise<void>) | (() => void) | string[] | boolean

  /**
   * Custom item reset event.
   */
  onReset?: () => void

  /**
   * Custom item selection event.
   * If it is false, the item will not be selectable.
   */
  onSelect?: (() => void) | boolean

  /**
   * Custom item hover event.
   * @param flag Mouse in or out.
   */
  onHover?: ((flag: boolean) => void) | boolean

  /**
   * Visibility toggle configuration. By default any panel item with `item` set
   * can be toggled so there's no need to set this.
   * - If it is FALSE, item visibility will not be togglable.
   * - If it is TRUE + isVirtualContainer, it will toggle all children.
   * - If it is NULL, it will display but not interactable.
   * - If it is an array, it will be used as a list of keys to toggle.
   * - If it is a function, that will be called instead with invisibility state derived from `item` + `hiddenElementIds`.
   * - If it is an object, it will treated as custom configuration consisting of:
   *   - invisible: boolean - current invisibility state
   *   - onToggle: function to be called when toggled
   *   - This is **HIGHLY** discouraged and should only be used for special cases.
   */
  toggleVisibility?:
    | { invisible: boolean; onToggle: (ids: string[], invisible: boolean) => void }
    | ((ids: string[], invisible: boolean) => void)
    | string[]
    | boolean
    | null
}

export interface EditorConfig {
  buttons?: EditorButtons
}

export enum ToolbarCategory {
  None,
  Navigate,
  Generate,
  Manipulate,
  Help,
}

export interface ElementsPanelConfig {
  getPanelItems?: (parent?: EditorPanelItem['item'], area?: InspectionArea) => EditorPanelItem[]
}

/**
 * Definition of a tool to be used by Editor.
 */
export interface EditorTool {
  /**
   * Tool key (must be from EDITOR_TOOLS constant)
   */
  key: string

  /**
   * Define the cursor state when this tool is active.
   * This cursor will be used when the current cursor state is 'Default'.
   */
  cursor?: CursorState

  /**
   * Check if the tool is allowed for the user.
   * This will show/hide the tool in the toolbar.
   *
   * Must be checked against WRITE permission.
   *
   * @param userProfile User's profile information.
   * @param isAllowedToModify Whether the user has modify permission on the project.
   *                          Tools that perform write operations must check this
   *                          so they stay hidden from e.g. invited users.
   * @returns
   */
  authCheck: (userProfile: UserInfo | null, isAllowedToModify: boolean) => boolean

  /**
   * Toolbar-related configurations
   */
  toolbar?: {
    /**
     * Tool icon. Must be a React component.
     * Define it first in assets/icons.tsx
     */
    icon: ReactElement

    /**
     * Tool label. Used for tooltip when hovering over the tool.
     */
    label: string

    /**
     * Tool variants configuration. Usually used for multiple tools under the same category.
     */
    variants?: EditorTool[]

    /**
     * Tool category. Used to group tools in the toolbar.
     * Default is ToolbarCategory.None.
     */
    category?: ToolbarCategory
  }

  /**
   * Tool hooks
   */
  hooks?: {
    /**
     * Hook to be executed by Editor.tsx
     * Usually used to define components to be shown on the canvas.
     * Context value is passed directly here since the hook is run _inside_ Editor.tsx
     * itself. The context has not been set yet, so useContext() will not work.
     */
    useEditor?: (contextValue: Editor) => EditorConfig

    /**
     * Hook for MainCanvas.
     * Most behavior will defined in this hook.
     *
     * @param props
     */
    useMainCanvas?: (props: CanvasProps) => CanvasConfig

    /**
     * Hook to return items for the objects panel.
     * When generating items at parent-level, make sure to check if the parent is undefined
     * and only return the items at this time.
     *
     * @param parent Parent item. If the item is a child, this will be the parent item.
     *               If the item is a parent, this will be undefined.
     *               Check EditorPanelItem['item'] for allowed types, but generally
     *               it must extend LayerStatus.
     */
    useElementsPanel?: () => ElementsPanelConfig
  }

  /**
   * Components to be added to various parts of the editor.
   */
  components?: {
    /**
     * Components to be added on the editor. This will be added outside of the canvas.
     * Use this to add modals, etc.
     * Must be a React component.
     */
    editor?: React.FC[]

    /**
     * Components to be added on the canvas.
     * Must be a THREE.js component.
     */
    canvas?: React.FC[]

    /**
     * Component to be added on the submission panel.
     * This panel will appear just before the submission button.
     * A tool can only have 1 panel at a time. If a tool has multiple panels for different states,
     * change the panel based on the state within the panel itself.
     */
    panel?: React.FC
  }

  /**
   * Configuration for the tool.
   */
  config?: {
    /**
     * Additional toolbars to be shown.
     */
    additionalToolbars?: {
      /**
       * Whether to use cuboid controls.
       */
      cuboidControls?: boolean
    }

    /**
     * Volume configuration.
     */
    volume?: {
      /**
       * Whether the tool requires a volume to be created.
       */
      required?: boolean

      /**
       * If volume can be selected.
       */
      selectable?: boolean

      /**
       * If only one volume can be selected.
       */
      onlyOneSelectable?: boolean
    }

    cylinder?: {
      /**
       * Whether the tool requires a cylinder to be created.
       */
      required?: boolean
    }

    /**
     * Config for selecting/placing anchors.
     */
    anchor?: {
      /**
       * Whether anchor can be placed on objects.
       * By default, anchors can be placed on objects.
       */
      notPlaceableOnObjects?: boolean

      /**
       * If there's a solid mesh, it will be used to place the anchor.
       * Otherwise, it will fall back to PCD.
       */
      useSolidMesh?: boolean

      /**
       * When enabled, anchor placement objects will always be used.
       * If no anchor placement objects are defined, point selection will return no point.
       */
      forceAnchorPlacedObjects?: boolean
    }

    /**
     * Type of shapes that can be selected by this tool.
     */
    selectableShapes?: ShapeKeyType[]

    /**
     * Inspection items that can be selected by this tool.
     */
    selectableInspectionItems?: InspectionItemType[]

    /**
     * These are tools that cannot be used at the same time with the current tool.
     * If the current tool is being saved, these tools will be disabled.
     *
     * Make sure to include itself if it should be locked when any of its
     * elements are being saved.
     */
    dependencies?: EditorToolKey[]
  }
}

export interface WithToolsConfig {
  toolsConfig: (
    | {
        key: string
        auth: boolean
        hook: EditorConfig
      }
    | undefined
  )[]
}
